import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useProductCache from 'store/cache/product-cache';

interface PrintLoginCardProps {
  product: string;
  firstName: string;
  lastName: string;
  educatorPin: string;
  username: string;
  password: string;
  onLoad: () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    card: {
      width: '50%',
      display: 'inline-block',
      margin: 0,
      padding: '15px 20px',
      borderWidth: '1px',
      borderStyle: 'dashed',
      boxShadow: 'none',
      boxSizing: 'border-box'
    },
    image: {
      maxHeight: '50px',
      maxWidth: '300px'
    },
    title: {
      color: theme.palette.grey[800],
      fontSize: theme.typography.pxToRem(17),
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: '100%',
      margin: `${theme.spacing(2.5)}px 0`
    },
    label: {
      display: 'inline-block'
    },
    text: {
      margin: 0,
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(16),
      fontFamily: theme.typography.fontFamily,
      '& span': {
        fontWeight: 300,
        fontSize: theme.typography.pxToRem(16)
      }
    }
  };
});

const PrintLoginCard = ({
  firstName,
  lastName,
  educatorPin,
  username,
  password,
  product,
  onLoad
}: PrintLoginCardProps): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('PrintLoginCard');
  const [{ findProductFamilyByCode }] = useProductCache();
  const productFamily = findProductFamilyByCode(product);

  return (
    <div className={classes.card}>
      {productFamily && (
        <img
          className={classes.image}
          src={productFamily.bannerUrl || ''}
          alt={productFamily.name}
          onLoad={onLoad}
        />
      )}
      <Typography variant="h2" className={classes.title}>{`${firstName} ${lastName}`}</Typography>
      <Typography variant="caption" component="p" className={classes.text}>
        <Typography variant="subtitle1" className={classes.label}>
          {t('Educator PIN')}:&nbsp;
        </Typography>
        {educatorPin}
      </Typography>
      <Typography variant="caption" component="p" className={classes.text}>
        <Typography variant="subtitle1" className={classes.label}>
          {t('Username')}:&nbsp;
        </Typography>
        {username}
      </Typography>
      <Typography variant="caption" component="p" className={classes.text}>
        <Typography variant="subtitle1" className={classes.label}>
          {t('Secret Code')}:&nbsp;
        </Typography>
        {password}
      </Typography>
    </div>
  );
};

export default PrintLoginCard;
